import React, { useState } from 'react';
import AzureIcon from '../../assets/Azure.svg';
import BingIcon from '../../assets/Bing.svg';
import Office365Icon from '../../assets/Office365.svg';
import { openFile } from '../../utils/fileOpenUtils';

export interface UnifiedCitationSource {
  index: number;
  marker: string;
  title: string;
  url: string;
  domain: string;
  snippet?: string;
  type?: string;
  metadata?: string;
}

interface Props {
  sources: UnifiedCitationSource[];
  locale: 'he' | 'en';
  onClose?: () => void;
}

// Get icon based on source type
const getSourceIcon = (type?: string, domain?: string): string => {
  // Office 365 sources
  if (type === 'email' || type === 'calendar' || type === 'file') {
    return Office365Icon;
  }
  
  // Azure/document sources - blob storage, research scopes, agentic retrieval
  const documentTypes = [
    'research_scope_source', 'azure_search', 'AzureSearchDoc', 'GroundingSource',
    'document', 'blob', 'internal_document', 'research_scope', 'agentic_source'
  ];
  if (type && documentTypes.some(t => type.toLowerCase().includes(t.toLowerCase()))) {
    return AzureIcon;
  }
  
  // Check domain for Office 365
  if (domain) {
    const d = domain.toLowerCase();
    if (d.includes('outlook') || d.includes('office365') || d.includes('sharepoint') || d.includes('onedrive') || d.includes('microsoft')) {
      return Office365Icon;
    }
    // Azure sources - blob storage or internal documents
    if (d.includes('azure') || d.includes('blob.core') || d.includes('internal') || d === 'document') {
      return AzureIcon;
    }
  }
  
  // Default to Bing for web search
  return BingIcon;
};

// Get label for source type
const getSourceLabel = (type?: string, domain?: string): string => {
  if (type === 'email') return 'Email';
  if (type === 'calendar') return 'Calendar';
  if (type === 'file') return 'File';
  
  // Document/Azure sources - research scopes, agentic retrieval, blob storage
  const documentTypes = [
    'research_scope_source', 'azure_search', 'AzureSearchDoc', 'GroundingSource',
    'document', 'blob', 'internal_document', 'research_scope', 'agentic_source'
  ];
  if (type && documentTypes.some(t => type.toLowerCase().includes(t.toLowerCase()))) {
    return 'Document';
  }
  
  if (domain) {
    const d = domain.toLowerCase();
    if (d.includes('outlook') || d.includes('office365')) return 'Office 365';
    if (d.includes('sharepoint')) return 'SharePoint';
    if (d.includes('onedrive')) return 'OneDrive';
    if (d.includes('azure') || d.includes('blob.core') || d.includes('internal') || d === 'document') return 'Document';
  }
  
  return 'Web';
};

export const CitationSourcesPanel: React.FC<Props> = ({ sources, locale }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (!sources || sources.length === 0) return null;

  // Handle click - directly open the source using shared utility
  const handleSourceClick = (source: UnifiedCitationSource) => {
    if (!source.url) return;
    // Use shared file opening utility - auto-detects file type
    openFile(source.url, source.title);
  };

  return (
    <div 
      style={{ 
        borderTop: '1px solid #e5e7eb',
        paddingTop: 12,
        marginTop: 16,
        position: 'relative'
      }} 
      dir={locale === 'he' ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 8, 
        marginBottom: 12 
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>
          {locale === 'he' ? 'מקורות' : 'Sources'}
        </span>
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          color: '#0078d4',
          background: 'rgba(0, 120, 212, 0.1)',
          padding: '2px 8px',
          borderRadius: 10
        }}>
          {sources.length}
        </span>
      </div>

      {/* Source circles - click to open, hover for tooltip */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: 10, 
        alignItems: 'center' 
      }}>
        {sources.map((src, arrayIndex) => {
          const isHovered = hoveredIndex === src.index;
          const icon = getSourceIcon(src.type, src.domain);
          const hasUrl = !!src.url;
          const typeLabel = getSourceLabel(src.type, src.domain);
          const isFirstItem = arrayIndex === 0;
          const isLastItem = arrayIndex === sources.length - 1;
          
          return (
            <div
              key={src.index}
              style={{ position: 'relative' }}
              onMouseEnter={() => setHoveredIndex(src.index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Source circle button */}
              <div
                onClick={() => hasUrl && handleSourceClick(src)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: isHovered ? 'rgba(0, 120, 212, 0.15)' : 'rgba(0, 120, 212, 0.05)',
                  border: `1.5px solid ${isHovered ? '#0078d4' : 'rgba(0, 120, 212, 0.3)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: hasUrl ? 'pointer' : 'default',
                  transition: 'all 0.15s ease',
                  transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                  boxShadow: isHovered ? '0 3px 10px rgba(0, 120, 212, 0.3)' : 'none',
                  position: 'relative',
                  opacity: hasUrl ? 1 : 0.6
                }}
              >
                {/* Icon */}
                <img 
                  src={icon} 
                  alt="" 
                  style={{ 
                    width: 18, 
                    height: 18,
                    opacity: isHovered ? 1 : 0.8
                  }} 
                />
                
                {/* Number badge */}
                <span style={{
                  position: 'absolute',
                  bottom: -3,
                  right: -3,
                  fontSize: 9,
                  fontWeight: 700,
                  color: 'white',
                  background: '#0078d4',
                  width: 15,
                  height: 15,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1.5px solid white',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)'
                }}>
                  {src.index}
                </span>
              </div>

              {/* Tooltip on hover - adjust position for edge items */}
              {isHovered && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    // Adjust horizontal positioning for edge items to prevent cutoff
                    ...(isFirstItem ? {
                      left: 0,
                      transform: 'none'
                    } : isLastItem ? {
                      right: 0,
                      transform: 'none'
                    } : {
                      left: '50%',
                      transform: 'translateX(-50%)'
                    }),
                    marginBottom: 8,
                    background: 'white',
                    color: '#1f2937',
                    padding: '10px 14px',
                    borderRadius: 8,
                    fontSize: 12,
                    maxWidth: 320,
                    minWidth: 180,
                    zIndex: 1000,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                    pointerEvents: 'none'
                  }}
                >
                  {/* Title - allow wrapping for long filenames */}
                  <div style={{ 
                    fontWeight: 600, 
                    marginBottom: 6,
                    wordBreak: 'break-word',
                    lineHeight: 1.4
                  }}>
                    {src.title}
                  </div>
                  
                  {/* Type and action hint */}
                  <div style={{ 
                    fontSize: 11, 
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    flexWrap: 'wrap'
                  }}>
                    <span style={{ 
                      background: '#f3f4f6', 
                      color: '#374151',
                      padding: '2px 8px', 
                      borderRadius: 4,
                      fontWeight: 500
                    }}>
                      {typeLabel}
                    </span>
                    {hasUrl && (
                      <span style={{ color: '#0078d4' }}>
                        {locale === 'he' ? 'לחץ לפתיחה' : 'Click to open'}
                      </span>
                    )}
                    {!hasUrl && (
                      <span style={{ color: '#9ca3af' }}>
                        {locale === 'he' ? 'אין קישור זמין' : 'No link available'}
                      </span>
                    )}
                  </div>
                  
                  {/* Arrow pointing down - position matches tooltip edge alignment */}
                  <div style={{
                    position: 'absolute',
                    bottom: -6,
                    // Arrow position follows tooltip alignment
                    ...(isFirstItem ? {
                      left: 18,
                      transform: 'none'
                    } : isLastItem ? {
                      right: 18,
                      transform: 'none'
                    } : {
                      left: '50%',
                      transform: 'translateX(-50%)'
                    }),
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid white',
                    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.1))'
                  }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CitationSourcesPanel;
